import React, { useState, useEffect } from 'react';
import { fetchArticle } from './api';
import { useTranslations } from './contexts/TranslationContext';

export default function PreviewModal({ articleId, onClose }) {
  var t = useTranslations();
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var articleState = useState(null);
  var article = articleState[0];
  var setArticle = articleState[1];

  var tabState = useState('id');
  var activeTab = tabState[0];
  var setActiveTab = tabState[1];

  useEffect(function() {
    fetchArticle(articleId)
      .then(function(data) { setArticle(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, [articleId]);

  // Close on Escape key
  useEffect(function() {
    function handleKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handleKey);
    return function() { document.removeEventListener('keydown', handleKey); };
  }, [onClose]);

  // Prevent body scroll
  useEffect(function() {
    document.body.style.overflow = 'hidden';
    return function() { document.body.style.overflow = ''; };
  }, []);

  var hasEnglish = article && article.content_en;
  var hasSeo = article && (article.meta_description || article.meta_keywords);
  var hasTags = article && article.tag_list && article.tag_list.length > 0;
  var hasCategories = article && article.category_list && article.category_list.length > 0;

  return (
    <div className="preview-modal__overlay" onClick={onClose}>
      <div className="preview-modal" onClick={function(e) { e.stopPropagation(); }}>
        {/* Header */}
        <div className="preview-modal__header">
          <h5>{t.preview || 'Preview'}</h5>
          <button type="button" className="preview-modal__close" onClick={onClose}>
            <i className="zmdi zmdi-close" />
          </button>
        </div>

        {/* Body */}
        <div className="preview-modal__body">
          {loading && (
            <div className="preview-modal__empty">
              <span className="spinner-border spinner-border-sm me-2" />
              Loading...
            </div>
          )}

          {!loading && !article && (
            <div className="preview-modal__empty">Article not found</div>
          )}

          {!loading && article && (
            <div className="preview-modal__article">
              {/* Cover image with overlay */}
              {article.image_url && (
                <div className="preview-modal__cover">
                  <img src={article.image_url} alt="" />
                  <div className="preview-modal__cover-overlay" />
                </div>
              )}

              <div className="preview-modal__info">
                {/* Status + Meta row */}
                <div className="preview-modal__top-row">
                  <span className={'preview-modal__status-badge preview-modal__status-badge--' + (article.published ? 'published' : 'draft')}>
                    {article.published ? t.published : t.draft}
                  </span>
                  {article.reading_time != null && (
                    <span className="preview-modal__reading-time">
                      <i className="zmdi zmdi-time" /> {article.reading_time} min read
                    </span>
                  )}
                </div>

                {/* Title */}
                <h2 className="preview-modal__title">{article.title_id}</h2>
                {article.title_en && (
                  <p className="preview-modal__title-en">{article.title_en}</p>
                )}

                {/* Meta */}
                <div className="preview-modal__meta">
                  {article.hospital_name && (
                    <span className="preview-modal__meta-item">
                      <i className="zmdi zmdi-hospital" /> {article.hospital_name}
                    </span>
                  )}
                  {article.doctor_name && (
                    <span className="preview-modal__meta-item">
                      <i className="zmdi zmdi-account" /> {article.doctor_name}
                    </span>
                  )}
                  {article.created_at && (
                    <span className="preview-modal__meta-item">
                      <i className="zmdi zmdi-calendar" /> {new Date(article.created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {/* Tags & Categories */}
                {(hasTags || hasCategories) && (
                  <div className="preview-modal__taxonomies">
                    {hasTags && article.tag_list.map(function(tag) {
                      return <span key={tag} className="preview-modal__tag">{tag}</span>;
                    })}
                    {hasCategories && article.category_list.map(function(cat) {
                      return <span key={cat} className="preview-modal__category">{cat}</span>;
                    })}
                  </div>
                )}

                <div className="preview-modal__divider" />

                {/* Content tabs */}
                {hasEnglish ? (
                  <div>
                    <div className="preview-modal__tabs">
                      <button
                        type="button"
                        className={'preview-modal__tab' + (activeTab === 'id' ? ' preview-modal__tab--active' : '')}
                        onClick={function() { setActiveTab('id'); }}
                      >
                        {t.tab_indonesian}
                      </button>
                      <button
                        type="button"
                        className={'preview-modal__tab' + (activeTab === 'en' ? ' preview-modal__tab--active' : '')}
                        onClick={function() { setActiveTab('en'); }}
                      >
                        {t.tab_english}
                      </button>
                    </div>
                    <div
                      className="preview-modal__content"
                      dangerouslySetInnerHTML={{ __html: activeTab === 'id' ? (article.content_id || '<p class="text-muted">-</p>') : article.content_en }}
                    />
                  </div>
                ) : (
                  <div
                    className="preview-modal__content"
                    dangerouslySetInnerHTML={{ __html: article.content_id || '<p class="text-muted">-</p>' }}
                  />
                )}

                {/* SEO section */}
                {hasSeo && (
                  <div className="preview-modal__seo">
                    <h6 className="preview-modal__seo-title">
                      <i className="zmdi zmdi-search" /> {t.seo}
                    </h6>
                    {article.meta_description && (
                      <div className="preview-modal__seo-field">
                        <span className="preview-modal__seo-label">{t.meta_description}</span>
                        <p className="preview-modal__seo-value">{article.meta_description}</p>
                      </div>
                    )}
                    {article.meta_keywords && (
                      <div className="preview-modal__seo-field">
                        <span className="preview-modal__seo-label">{t.meta_keywords}</span>
                        <p className="preview-modal__seo-value">{article.meta_keywords}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
